import React from 'react';
import './gene.css';

function ReadMePage() {
    return (
        <div className="gene-page" >
            <div className="gene-main-content">
                <div className="gene-glass-card" style={{ maxWidth: '1000px', margin: '0 auto' }}>
                    <div className="gene-card-title" style={{ textAlign: 'center', marginBottom: '2rem' }}>
                        Read Me
                    </div>
                    
                    <div style={{ lineHeight: '1.8', fontSize: '1rem', color: '#374151' }}>
                        <p style={{ marginBottom: '1.5rem', fontSize: '1.1rem' }}>
                            This web platform supports the visualization of data and insights from the study titled 
                            <strong> "A Whole Blood Transcriptomic Atlas Across 98 Health Conditions Reveals Systemic Disease Signatures."</strong>
                        </p>
                        
                        <p style={{ marginBottom: '2rem' }}>
                            The resource provides comprehensive transcriptomic data and interactive exploration tools across the following sections:
                        </p>

                        <div style={{ marginBottom: '2rem' }}>
                            <h3 style={{ color: '#1f2937', marginBottom: '1rem', fontSize: '1.3rem' }}>1) Gene</h3>
                            <p style={{ marginBottom: '1rem', paddingLeft: '1rem' }}>
                                This section presents Whole Blood Transcriptomic (WBT) features at the single-gene level. Users can explore the data in two modes:
                            </p>
                            <ul style={{ paddingLeft: '2rem', marginBottom: '1rem' }}>
                                <li style={{ marginBottom: '0.5rem' }}>
                                    <strong>Gene-specific mode:</strong> Visualizes the expression levels of a selected gene across different health conditions, 
                                    along with differential expression statistics compared to healthy controls.
                                </li>
                                <li style={{ marginBottom: '0.5rem' }}>
                                    <strong>Disease-specific mode:</strong> Identifies the most significantly altered genes for each disease condition, 
                                    allowing users to examine disease-specific transcriptomic signatures.
                                </li>
                            </ul>
                        </div>

                        <div style={{ marginBottom: '2rem' }}>
                            <h3 style={{ color: '#1f2937', marginBottom: '1rem', fontSize: '1.3rem' }}>2) Pathway</h3>
                            <p style={{ marginBottom: '1rem', paddingLeft: '1rem' }}>
                                This section displays WBT features at the pathway level. Similar to the gene section, users can explore the data in either:
                            </p>
                            <ul style={{ paddingLeft: '2rem', marginBottom: '1rem' }}>
                                <li style={{ marginBottom: '0.5rem' }}><strong>Pathway-specific mode</strong> or</li>
                                <li style={{ marginBottom: '0.5rem' }}><strong>Disease-specific mode</strong></li>
                            </ul>
                            <p style={{ paddingLeft: '1rem' }}>
                                Pathway enrichment analysis was performed using gene sets from the MSigDB database.
                            </p>
                        </div>

                        <div style={{ marginBottom: '2rem' }}>
                            <h3 style={{ color: '#1f2937', marginBottom: '1rem', fontSize: '1.3rem' }}>3) Co-expression</h3>
                            <p style={{ paddingLeft: '1rem' }}>
                                This section displays gene co-expression clusters—groups of genes that show high expression correlation across the 4,444 whole blood samples. 
                                For each cluster, users can explore its gene composition, biological relevance, and eigengene values (representing the dominant expression pattern of the cluster) 
                                across different health conditions.
                            </p>
                        </div>

                        <div style={{ marginBottom: '2rem' }}>
                            <h3 style={{ color: '#1f2937', marginBottom: '1rem', fontSize: '1.3rem' }}>4) Aging</h3>
                            <p style={{ paddingLeft: '1rem' }}>
                                This section highlights genes and gene clusters that show strong correlation with aging. Users can also explore the relationship between 
                                immune cell composition and metabolic reaction activity with age.
                            </p>
                        </div>

                        <div style={{ marginBottom: '2rem' }}>
                            <h3 style={{ color: '#1f2937', marginBottom: '1rem', fontSize: '1.3rem' }}>5) Immune</h3>
                            <p style={{ paddingLeft: '1rem' }}>
                                This section presents immune cell composition and TCR/BCR repertoire diversity across different health conditions.
                            </p>
                        </div>

                        <div style={{ marginBottom: '2rem' }}>
                            <h3 style={{ color: '#1f2937', marginBottom: '1rem', fontSize: '1.3rem' }}>6) Metabolic</h3>
                            <p style={{ paddingLeft: '1rem' }}>
                                This section presents metabolic activity profiles across different health conditions, analyzed at both the reaction and subsystem levels.
                            </p>
                        </div>

                        <div style={{ marginBottom: '2rem' }}>
                            <h3 style={{ color: '#1f2937', marginBottom: '1rem', fontSize: '1.3rem' }}>7) Prediction</h3>
                            <p style={{ paddingLeft: '1rem' }}>
                                This section displays the top 10 most important gene features identified for the classification of each disease, along with their expression patterns 
                                across different conditions. The multi-class classification model built using these gene features demonstrated strong predictive performance, 
                                which was also validated in independent cohorts.
                            </p>
                        </div>

                        <div style={{ marginTop: '3rem', padding: '1.5rem', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                            <h3 style={{ color: '#1f2937', marginBottom: '1rem', fontSize: '1.3rem' }}>Technical Implementation</h3>
                            <p style={{ marginBottom: '1rem' }}>
                                This web platform was built using <strong>React</strong> and modern web technologies, featuring advanced performance optimizations 
                                and user experience enhancements:
                            </p>
                            <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                                <li style={{ marginBottom: '0.5rem' }}>
                                    <strong>Intelligent Data Caching:</strong> Implements multi-layer caching strategies including browser-level caching, 
                                    API response caching, and dynamic data prefetching to minimize load times
                                </li>
                                <li style={{ marginBottom: '0.5rem' }}>
                                    <strong>High-Performance Server Architecture:</strong> Utilizes optimized backend APIs with compressed data transfer, 
                                    efficient database indexing, and parallel query processing for rapid data retrieval
                                </li>
                                <li style={{ marginBottom: '0.5rem' }}>
                                    <strong>Interactive Visualization Engine:</strong> Leverages Plotly.js for real-time, responsive data visualizations 
                                    with hardware-accelerated rendering and adaptive resolution scaling
                                </li>
                                <li style={{ marginBottom: '0.5rem' }}>
                                    <strong>Responsive Design Framework:</strong> Built with CSS Grid and Flexbox for seamless cross-device compatibility 
                                    and optimal viewing experience across desktop, tablet, and mobile platforms
                                </li>
                                <li style={{ marginBottom: '0.5rem' }}>
                                    <strong>Advanced State Management:</strong> Implements React hooks and context patterns for efficient component 
                                    state synchronization and minimal re-rendering overhead
                                </li>
                            </ul>
                            
                            <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: '#fef3c7', borderRadius: '6px', border: '1px solid #f59e0b' }}>
                                <p style={{ margin: '0', fontSize: '0.95rem', color: '#92400e' }}>
                                    <strong>Usage License:</strong> Please note that the use of this web tool is limited to academic, non-commercial use only. 
                                    For commercial applications or licensing inquiries, please contact the research team.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ReadMePage;
